"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type SharePlatform =
  "facebook" | "twitter" | "linkedin" | "whatsapp" | "telegram" | "email";

export interface IUseShareOptions {
  url?: string; // falls back to window.location.href on client
  title?: string;
  text?: string;
  autoCloseDelayMs?: number; // delay before closing dropdown after copy
}

export interface IUseShare {
  // state
  isOpen: boolean;
  isCopied: boolean;
  isNativeSupported: boolean;
  canCopy: boolean;
  fullUrl: string;

  // actions
  open: () => void;
  close: () => void;
  toggle: () => void;
  share: () => Promise<void>; // native share if available, else open()
  copy: () => Promise<boolean>; // clipboard copy
  shareTo: (platform: SharePlatform) => void;

  // helpers
  getShareUrl: (p: SharePlatform) => string;
}

export function useShare(opts: IUseShareOptions = {}): IUseShare {
  const {
    title = "Check this out!",
    text = "Found this amazing place!",
    autoCloseDelayMs = 1500,
  } = opts;

  const clientUrl = typeof window !== "undefined" ? window.location.href : "";
  const fullUrl = useMemo(
    () => opts.url || clientUrl || "",
    [opts.url, clientUrl],
  );

  const [isOpen, setOpen] = useState(false);
  const [isCopied, setCopied] = useState(false);
  const [isNativeSupported, setNativeSupported] = useState(false);
  const [canCopy, setCanCopy] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Feature detection (client only)
    setNativeSupported(
      typeof navigator !== "undefined" && typeof navigator.share === "function",
    );
    setCanCopy(typeof navigator !== "undefined" && !!navigator.clipboard);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  const share = useCallback(async () => {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function"
    ) {
      try {
        await navigator.share({ title, text, url: fullUrl });
        setOpen(false);
        return;
      } catch {
        // user cancelled or share failed — fall back to dropdown
      }
    }
    setOpen(true);
  }, [title, text, fullUrl]);

  const copy = useCallback(async () => {
    if (!canCopy) return false;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setCopied(false);
        setOpen(false);
      }, autoCloseDelayMs) as unknown as number;
      return true;
    } catch {
      return false;
    }
  }, [canCopy, fullUrl, autoCloseDelayMs]);

  const getShareUrl = useCallback(
    (platform: SharePlatform) => {
      const u = encodeURIComponent(fullUrl);
      const t = encodeURIComponent(title);
      const x = encodeURIComponent(text);
      switch (platform) {
        case "facebook":
          return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
        case "twitter":
          return `https://twitter.com/intent/tweet?url=${u}&text=${x}`;
        case "linkedin":
          return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
        case "whatsapp":
          return `https://wa.me/?text=${x}%20${u}`;
        case "telegram":
          return `https://t.me/share/url?url=${u}&text=${x}`;
        case "email":
          return `mailto:?subject=${t}&body=${x}%20${u}`;
      }
    },
    [fullUrl, title, text],
  );

  const shareTo = useCallback(
    (platform: SharePlatform) => {
      const href = getShareUrl(platform);
      if (typeof window !== "undefined") {
        window.open(href, "_blank", "width=600,height=480");
      }
      setOpen(false);
    },
    [getShareUrl],
  );

  return {
    isOpen,
    isCopied,
    isNativeSupported,
    canCopy,
    fullUrl,
    open,
    close,
    toggle,
    share,
    copy,
    shareTo,
    getShareUrl,
  };
}
